import { BlobStore, LocalStore, type StoreName } from '@/platform/localStore';
import { Platform } from '@/platform';
import { SYNC } from '@/contracts/brand';

/**
 * Cloud sync (spec §15.3).
 *
 * This is what makes "the web app and the Apple app are in sync" a true
 * sentence rather than a hope. Everything about it is arranged around one
 * belief: the device the child is holding is the source of truth for what they
 * just did, and the network is a courier that may be late.
 *
 * The rules:
 *
 *  1. LOCAL FIRST, ALWAYS. Nothing here is on the path between a child's
 *     finger and their picture being saved. Every write lands in IndexedDB
 *     first (§9.3) and is queued here afterwards. Sync being down, slow or
 *     switched off changes nothing about drawing.
 *
 *  2. THE OUTBOX IS THE QUEUE. A queued change survives a force-quit, a flat
 *     battery and a week in a drawer, because it is a durable record, not an
 *     in-memory promise.
 *
 *  3. REVISIONS, NOT CLOCKS. Two devices editing offline are resolved by
 *     revision numbers, never by comparing wall clocks that were never in
 *     agreement. When the server has moved on, the push is refused and the
 *     server's copy comes back; artwork keeps BOTH as separate revisions
 *     rather than choosing a winner, because a child's picture is not a
 *     mergeable document (§9.4).
 *
 *  4. HONEST CLAIMS. The UI may say "Saved everywhere" only after the server
 *     has acknowledged the write. Until then it says "Saved on this device",
 *     which is exactly what has happened.
 *
 *  5. NOT CONFIGURED MEANS ABSENT. With no service URL there is no sign-in
 *     screen, no sync switch and no "coming soon" — `CAPABILITIES.cloudSync`
 *     is false and the product behaves as the local-only app it then is.
 */

export interface SyncSession {
  token: string;
  userId: string;
  deviceId: string;
  expiresAt: number;
  email: string;
}

/** Records that travel. Drafts stay local: a draft is a device's scratchpad. */
const SYNCED: Array<{ store: StoreName; kind: string }> = [
  { store: 'children', kind: 'child' },
  { store: 'progress', kind: 'progress' },
  { store: 'artworks', kind: 'artwork' },
  { store: 'revisions', kind: 'revision' },
  { store: 'editions', kind: 'edition' },
  { store: 'placements', kind: 'placement' },
];

const KIND_TO_STORE = new Map(SYNCED.map((s) => [s.kind, s.store]));

interface Queued {
  id: string;
  store: StoreName;
  kind: string;
  childId: string | null;
  /** The revision this device started from, for conflict detection. */
  baseRevision: number;
  queuedAt: number;
  attempts: number;
  lastError: string | null;
}

export type SyncState =
  | { kind: 'off' }
  | { kind: 'signed-out' }
  | { kind: 'idle'; at: number; pending: number }
  | { kind: 'working' }
  | { kind: 'offline'; pending: number }
  | { kind: 'failed'; message: string; pending: number };

type Listener = (state: SyncState) => void;

let state: SyncState = SYNC.baseUrl ? { kind: 'signed-out' } : { kind: 'off' };
const listeners = new Set<Listener>();
let session: SyncSession | null = null;
let account = 'guest';
let running = false;
/** Set when something is queued while a run is in flight (see `run`). */
let dirty = false;

function emit(next: SyncState) {
  state = next;
  for (const l of listeners) l(next);
}

async function api(path: string, init: RequestInit = {}): Promise<Response> {
  if (!SYNC.baseUrl) throw new Error('sync-not-configured');
  const headers = new Headers(init.headers);
  headers.set('x-device-id', await deviceId());
  if (session) headers.set('authorization', `Bearer ${session.token}`);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  return fetch(`${SYNC.baseUrl}${path}`, { ...init, headers });
}

/** A stable per-device id, generated once and kept with the device's data. */
async function deviceId(): Promise<string> {
  const existing = await LocalStore.get<string>('meta', 'device', 'id');
  if (existing) return existing;
  const id = `dev_${crypto.randomUUID()}`;
  await LocalStore.put('meta', 'device', 'id', id);
  return id;
}

export const SyncService = {
  get available() {
    return Boolean(SYNC.baseUrl);
  },

  get state() {
    return state;
  },

  get session() {
    return session;
  },

  subscribe(listener: Listener) {
    listeners.add(listener);
    listener(state);
    return () => listeners.delete(listener);
  },

  /** Restores a saved session on boot. Never throws — sync is best-effort. */
  async restore(localAccount: string) {
    account = localAccount;
    if (!SYNC.baseUrl) return;
    try {
      const saved = await LocalStore.get<SyncSession>('meta', 'sync', 'session');
      if (saved && saved.expiresAt > Date.now()) {
        session = saved;
        emit({ kind: 'idle', at: 0, pending: await pendingCount() });
        void SyncService.run();
      } else {
        emit({ kind: 'signed-out' });
      }
    } catch {
      emit({ kind: 'signed-out' });
    }
  },

  async register(email: string, password: string): Promise<{ ok: true } | { ok: false; error: string }> {
    return enter('/auth/register', email, password);
  },

  async signIn(email: string, password: string): Promise<{ ok: true } | { ok: false; error: string }> {
    return enter('/auth/login', email, password);
  },

  async signOut() {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      /* signing out locally matters more than telling the server */
    }
    session = null;
    await LocalStore.delete('meta', 'sync', 'session');
    emit({ kind: 'signed-out' });
  },

  /**
   * Queues a record for the server.
   *
   * Called AFTER the local write has resolved, never before, and it does not
   * await the network.
   */
  async queue(store: StoreName, id: string, opts: { childId?: string | null; baseRevision?: number } = {}) {
    if (!SYNC.baseUrl) return;
    const kind = SYNCED.find((s) => s.store === store)?.kind;
    if (!kind) return;
    const entry: Queued = {
      id,
      store,
      kind,
      childId: opts.childId ?? null,
      // The revision this device last had acknowledged for this record. Using
      // 0 every time would make the device conflict with its own last write.
      baseRevision: opts.baseRevision ?? (await knownRevision(store, id)),
      queuedAt: Date.now(),
      attempts: 0,
      lastError: null,
    };
    try {
      await LocalStore.put('outbox', account, `${store}:${id}`, entry);
      // A run already in flight will not see this entry, so remember to go
      // round once more rather than leaving it until the next trigger.
      if (running) dirty = true;
    } catch {
      // A full disk must not break drawing. The record is still on the device;
      // a later successful queue or a full re-scan will pick it up.
      return;
    }
    if (session) void SyncService.run();
  },

  /** Pushes everything queued, then pulls whatever the other device did. */
  async run(): Promise<void> {
    if (!SYNC.baseUrl || !session || running) return;
    running = true;
    try {
      if (!Platform.connectivity.online) {
        emit({ kind: 'offline', pending: await pendingCount() });
        return;
      }
      emit({ kind: 'working' });
      // Loop while writes keep arriving, so a change queued mid-run is sent in
      // this sync rather than waiting for the next one. Bounded, because a
      // child drawing continuously must not keep it spinning forever — the
      // remainder goes on the next trigger.
      for (let pass = 0; pass < 3; pass++) {
        dirty = false;
        await pushOutbox();
        if (!dirty) break;
      }
      await pullChanges();
      emit({ kind: 'idle', at: Date.now(), pending: await pendingCount() });
    } catch (err) {
      const offline = !Platform.connectivity.online;
      const pending = await pendingCount();
      emit(
        offline
          ? { kind: 'offline', pending }
          : { kind: 'failed', message: describe(err), pending },
      );
    } finally {
      running = false;
    }
  },
};

async function enter(path: string, email: string, password: string) {
  try {
    const res = await api(path, { method: 'POST', body: JSON.stringify({ email, password }) });
    const data = (await res.json()) as { token?: string; userId?: string; expiresAt?: number; error?: string };
    if (!res.ok || !data.token || !data.userId) {
      return { ok: false as const, error: friendly(data.error ?? 'sign-in-failed') };
    }
    session = {
      token: data.token,
      userId: data.userId,
      deviceId: await deviceId(),
      expiresAt: data.expiresAt ?? Date.now() + 30 * 24 * 3600_000,
      email,
    };
    await LocalStore.put('meta', 'sync', 'session', session);
    // Everything already on this device is queued, so signing in on a second
    // device brings the work with it rather than starting empty.
    await queueEverything();
    void SyncService.run();
    return { ok: true as const };
  } catch {
    return { ok: false as const, error: 'Could not reach the service. Your work is safe on this device.' };
  }
}

/** Sweeps every syncable record into the outbox. */
async function queueEverything() {
  for (const { store, kind } of SYNCED) {
    const rows = await LocalStore.list<{ id?: string; childId?: string | null; revision?: number }>(store, account);
    for (const row of rows) {
      if (!row?.id) continue;
      await LocalStore.put('outbox', account, `${store}:${row.id}`, {
        id: row.id,
        store,
        kind,
        childId: row.childId ?? null,
        baseRevision: 0,
        queuedAt: Date.now(),
        attempts: 0,
        lastError: null,
      } satisfies Queued);
    }
  }
}

async function pendingCount(): Promise<number> {
  try {
    return (await LocalStore.list<Queued>('outbox', account)).length;
  } catch {
    return 0;
  }
}

async function pushOutbox() {
  const queued = await LocalStore.list<Queued>('outbox', account);
  if (queued.length === 0) return;

  // Batched, because a child who colours for twenty minutes offline comes back
  // with hundreds of changes and each one should not be a round trip.
  for (let i = 0; i < queued.length; i += 100) {
    const batch = queued.slice(i, i + 100);
    const records = [];
    for (const q of batch) {
      const body = await LocalStore.get<Record<string, unknown>>(q.store, account, q.id);
      if (!body) {
        // Gone locally: tell the server it is gone rather than leaving a
        // record that outlives what it described.
        records.push({ id: q.id, kind: q.kind, childId: q.childId, body: {}, deleted: true, baseRevision: q.baseRevision });
        continue;
      }
      // A revision without its picture is not a synced picture. The bytes go
      // first, so the record never points at something the other device
      // cannot fetch.
      if (q.kind === 'revision') await uploadRevisionBlobs(body as unknown as RevisionLike);
      records.push({
        id: q.id,
        kind: q.kind,
        childId: q.childId,
        body,
        baseRevision: q.baseRevision,
      });
    }

    const res = await api('/sync', { method: 'POST', body: JSON.stringify({ records }) });
    if (res.status === 401) {
      await SyncService.signOut();
      throw new Error('signed-out');
    }
    if (!res.ok) throw new Error(`push-failed-${res.status}`);
    const data = (await res.json()) as {
      accepted: Array<{ id: string; revision: number }>;
      conflicts: Array<{ id: string; server: ServerRecord }>;
    };

    const accepted = new Map(data.accepted.map((a) => [a.id, a.revision]));
    for (const q of batch) {
      const revision = accepted.get(q.id);
      if (revision === undefined) continue;
      await rememberRevision(q.store, q.id, revision);
      // Delete the entry ONLY if it is still the one that was just sent.
      //
      // A child pressing Done writes the revision and then the artwork, and
      // each write queues. If the second write lands while this push is in
      // flight, deleting by key alone would throw away a change that was never
      // sent — the picture would be finished on this device and forever a
      // draft everywhere else. Comparing the stamp makes the delete an
      // acknowledgement of a specific version rather than of a name.
      const current = await LocalStore.get<Queued>('outbox', account, `${q.store}:${q.id}`);
      if (current && current.queuedAt !== q.queuedAt) continue;
      await LocalStore.delete('outbox', account, `${q.store}:${q.id}`);
    }
    for (const conflict of data.conflicts) {
      const q = batch.find((b) => b.id === conflict.id);
      if (!q) continue;
      await resolveConflict(conflict.server, q);
      // The device now knows the server's revision, so the retry is not stale.
      // It stays queued with the LOCAL body: this device is the one the family
      // is holding, and it has just been told what the server thinks.
      await LocalStore.put('outbox', account, `${q.store}:${q.id}`, {
        ...q,
        baseRevision: conflict.server.revision,
        queuedAt: Date.now(),
        attempts: q.attempts + 1,
        lastError: 'conflict',
      } satisfies Queued);
      await rememberRevision(q.store, q.id, conflict.server.revision);
      dirty = true;
    }
  }
}

/** The server revision this device last had acknowledged for a record. */
async function knownRevision(store: StoreName, id: string): Promise<number> {
  return (await LocalStore.get<number>('meta', account, `syncrev:${store}:${id}`)) ?? 0;
}

async function rememberRevision(store: StoreName, id: string, revision: number) {
  await LocalStore.put('meta', account, `syncrev:${store}:${id}`, revision);
}

interface RevisionLike {
  previewRef: string | null;
  strokeLayerRef: string | null;
}

/**
 * Uploads the picture bytes a revision refers to.
 *
 * `previewRef` is a flattened PNG blob; `strokeLayerRef` is the raster brush
 * layer, stored as a data URL. Both are content the child made, so both travel.
 * Blob ids are derived from the revision id, which makes the upload idempotent:
 * sending it twice overwrites identical bytes rather than creating a second
 * copy on the server.
 */
async function uploadRevisionBlobs(rev: RevisionLike) {
  if (rev.previewRef) {
    const blob = await BlobStore.get(account, rev.previewRef);
    if (blob) {
      await api(`/blobs?id=${encodeURIComponent(rev.previewRef)}`, {
        method: 'POST',
        headers: { 'content-type': blob.type || 'image/png' },
        body: blob,
      });
    }
  }
  if (rev.strokeLayerRef) {
    const dataUrl = await LocalStore.get<string>('meta', account, rev.strokeLayerRef);
    if (dataUrl) {
      await api(`/blobs?id=${encodeURIComponent(rev.strokeLayerRef)}`, {
        method: 'POST',
        headers: { 'content-type': 'text/plain' },
        body: dataUrl,
      });
    }
  }
}

/** Fetches a revision's picture bytes if this device does not have them. */
async function downloadRevisionBlobs(rev: RevisionLike) {
  if (rev.previewRef && !(await BlobStore.get(account, rev.previewRef))) {
    const res = await api(`/blobs/${encodeURIComponent(rev.previewRef)}`);
    if (res.ok) await BlobStore.put(account, rev.previewRef, await res.blob());
  }
  if (rev.strokeLayerRef && !(await LocalStore.get<string>('meta', account, rev.strokeLayerRef))) {
    const res = await api(`/blobs/${encodeURIComponent(rev.strokeLayerRef)}`);
    if (res.ok) await LocalStore.put('meta', account, rev.strokeLayerRef, await res.text());
  }
}

interface ServerRecord {
  id: string;
  kind: string;
  childId: string | null;
  body: Record<string, unknown>;
  revision: number;
  deleted: boolean;
  deviceId: string;
}

/**
 * What to do when two devices changed the same record.
 *
 * The rule is: THE DEVICE IN THE FAMILY'S HANDS WINS, and no picture is ever
 * lost either way.
 *
 * That is safe because of how the data is shaped. A picture is an
 * `ArtworkRevision`: immutable, with an id generated where it was made. Two
 * devices drawing offline produce two revisions with two ids, so they never
 * collide — both arrive, and both are kept (§9.4). What CAN collide is the
 * small metadata around them: which revision an artwork points at, a nickname,
 * a page in an edition. For those, the copy on the device someone is using
 * right now is the more recent intent, and taking it costs nothing that cannot
 * be seen and changed on screen.
 *
 * The one case where the server wins is a deletion: a record deleted elsewhere
 * stays deleted rather than being resurrected by an old device.
 *
 * The local body is therefore left alone here and stays queued; this only
 * records what the server said, so the retry is no longer stale.
 */
async function resolveConflict(server: ServerRecord, queued: Queued) {
  const store = KIND_TO_STORE.get(server.kind);
  if (!store) return;
  if (server.deleted) {
    await LocalStore.delete(store, account, server.id);
    await LocalStore.delete('outbox', account, `${queued.store}:${queued.id}`);
  }
}

async function pullChanges() {
  let cursor = (await LocalStore.get<number>('meta', account, 'sync-cursor')) ?? 0;
  for (let page = 0; page < 50; page++) {
    const res = await api(`/sync?since=${cursor}`);
    if (res.status === 401) {
      await SyncService.signOut();
      throw new Error('signed-out');
    }
    if (!res.ok) throw new Error(`pull-failed-${res.status}`);
    const data = (await res.json()) as { cursor: number; more: boolean; records: ServerRecord[] };

    for (const rec of data.records) {
      // A record this device just pushed comes back on the next pull; applying
      // it is harmless but pointless, and skipping it avoids a write storm.
      if (rec.deviceId === session?.deviceId) continue;
      const store = KIND_TO_STORE.get(rec.kind);
      if (!store) continue;
      if (rec.deleted) {
        await LocalStore.delete(store, account, rec.id);
        await rememberRevision(store, rec.id, rec.revision);
        continue;
      }
      // `ownerUserId` is the LOCAL account namespace, and every device has its
      // own. The value that travelled belongs to the device that made the
      // record; keeping it here would make the record fail this device's own
      // ownership check (§3) and the child would appear in the chooser but be
      // unreachable. The server's ownership is the authority on the wire; the
      // local one is the authority on the device.
      await LocalStore.put(store, account, rec.id, {
        ...rec.body,
        id: rec.id,
        ...(('ownerUserId' in rec.body) ? { ownerUserId: account } : {}),
      });
      await rememberRevision(store, rec.id, rec.revision);
      // Fetch the picture behind a revision, once, and only if this device
      // does not already have it.
      if (rec.kind === 'revision') {
        await downloadRevisionBlobs(rec.body as unknown as RevisionLike);
      }
    }

    cursor = data.cursor;
    await LocalStore.put('meta', account, 'sync-cursor', cursor);
    if (!data.more) break;
  }
}

function describe(err: unknown): string {
  const message = (err as Error)?.message ?? '';
  if (message === 'signed-out') return 'Signed out. Sign in again to keep things in sync.';
  if (message.startsWith('push') || message.startsWith('pull')) {
    return 'The service could not be reached. Everything is still on this device.';
  }
  return 'Sync did not finish. Everything is still on this device.';
}

function friendly(code: string): string {
  switch (code) {
    case 'email-taken':
      return 'There is already an account with that email.';
    case 'email-invalid':
      return 'That does not look like an email address.';
    case 'password-too-short':
      return 'Please use a password of at least 10 characters.';
    default:
      return 'That email and password did not match an account.';
  }
}
